// import {isParsleyForm, isValid} from "../parsley-bootstrap-validation";
import {getPrefixedDataSet} from "../data-set-utils";
import 'url-polyfill';
import 'url-search-params-polyfill'; // Edge Polyfill
import fetch from '../fetch'; // IE10 Polyfill
import {debounce} from "debounce";
import asyncAppend from '../async-append';
import formDataEntries from 'form-data-entries';
import {onFind} from "../init-modules-in-scope";
import {findAllIn, findIn, on, is, off, trigger, triggerWith, setAttribute, removeAttribute,closest} from "../dom-utils";

const defaultSelectors = {
    base: '.js-ajax-form',
    result: '.js-ajax-form__result',
    loading: '.js-ajax-form__loading',
    notifications: '.js-ajax-form__notifications',
    form: '.js-ajax-form__form',
    additionalForm: '.js-ajax-form__additional-form',
    errorArea: '.js-ajax-form__error-area',
    retry: '.js-ajax-form__retry',
    link: '.js-ajax-form__link',
    reset: '.js-ajax-form__reset'
};

const defaultOptions = {
    submitOnChange: false,
    addUrlParams: false,
    fetchHeaders: {},
    validator: () => true,
    onSubmit: () => {}
};

export function init(options = defaultOptions, selectors = defaultSelectors) {
    onFind(selectors.base, function (baseElement) {
        createAjaxForm(
            baseElement,
            {...defaultOptions, ...options},
            {...defaultSelectors, ...selectors}
        );
    });
}

export function createAjaxForm(baseElement, options = defaultOptions, selectors = defaultSelectors) {
    let pendingRequest = null;
    let form = findIn(selectors.form, baseElement);
    let results = findAllIn(selectors.result, baseElement);
    let loadingElements = findAllIn(selectors.loading, baseElement);
    let notifications = findAllIn(selectors.notifications, baseElement);
    let errorAreas = findAllIn(selectors.errorArea, baseElement);

    options = {
        ...defaultOptions,
        ...options,
        ...transformDataSetOptions(getPrefixedDataSet('ajax-form', baseElement))
    };

    // form
    onFind(selectors.form, function (form) {
        const submitHandler = evt => {
            evt.preventDefault();
            evt.stopImmediatePropagation(); // otherwise .on('submit.ajax-form') would be called twice
            submitForm(form);
        };

        const changeHandler = debounce(() => {
            submitForm(form);
        }, 200);

        on('submit', submitHandler, form);

        if (options.submitOnChange) {
            on('change', changeHandler, form);
        }

        return () => {
            off('submit', submitHandler, form);
            off('change', changeHandler, form);
        }
    }, baseElement);

    // additional forms
    onFind(selectors.additionalForm, function (additionalForm) {
        const submitHandler = evt => {
            evt.preventDefault();
            submitForm(additionalForm);
        };

        const changeHandler = debounce(function () {
            submitForm(additionalForm);
        }, 200);

        on('submit', submitHandler, additionalForm);

        if (options.submitOnChange || additionalForm.dataset.ajaxFormSubmitOnChange) {
            on('change', changeHandler, additionalForm)
        }

        return () => {
            off('submit', submitHandler, additionalForm);
            off('change', changeHandler, additionalForm);
        }
    }, baseElement);

    // retry
    findAllIn(selectors.retry, baseElement)
        .map(on('click', (evt) => {
            evt.preventDefault();

            if (lastLoadParams) {
                load(...lastLoadParams);
            }
        }));

    // links
    onFind(selectors.link, (link) => {
        function linkClickHandler(evt) {
            evt.preventDefault();

            let closestForm = closest('form', link);

            let href = link.getAttribute('href') || link.dataset.href;
            let action = closestForm ? closestForm.dataset.action || closestForm.getAttribute('action') : form.dataset.action || form.getAttribute('action');
            let params = new URL(href, location.origin).searchParams;

            pendingRequest = load(action, 'GET', params, href);

            pendingRequest
                .then(() => pendingRequest = null)
                .catch((error) => {
                    if ((!error || error.name !== 'AbortError')) {
                        pendingRequest = null;
                    }
                });
        }

        on('click', linkClickHandler, link);

        return () => {
            off('click', linkClickHandler, link);
        }
    }, baseElement);


    // Reset buttons
    onFind(selectors.reset, (resetButton) => {
        function resetClickHandler(evt) {
            let inputs = findAllIn('input, select', form);
            let resetName = evt.target.dataset.resetName;
            let resetValue =  evt.target.dataset.resetValue;

            evt.preventDefault();

            if (resetName) {
                let selectorName = resetName
                    .split(',')
                    .map(name => `[name="${name.trim()}"]`)
                    .join(', ');

                inputs = inputs.filter(is(selectorName));
            }

            if (resetValue) {
                let selectorValue = resetValue.toString()
                    .split(',')
                    .map(value => `[value="${value.trim()}"]`)
                    .join(', ');

                inputs = inputs.filter(is(selectorValue));
            }


            inputs.map(function(input) {
                if(input.type !== 'radio' && input.type !== 'checkbox') {
                    input.value = '';
                }
            });
            inputs.filter(is('[type="radio"], [type="checkbox"]')).map(input => input.checked = false);
            inputs.map(trigger('change'));
            trigger('reset.ajax-form', baseElement);
        }

        on('click', resetClickHandler, resetButton);

        return () => {
            off('click', resetClickHandler, resetButton);
        }
    }, baseElement);


    let lastLoadParams = null; // for retry
    function load(url, method, params, historyUrl) {
        method = method || "GET";
        lastLoadParams = [url, method, params, historyUrl];

        if (options.addUrlParams) {
            history.replaceState(history.state, document.title, historyUrl || url);
        }

        // add base url to params (path)
        params.append('baseUrl', location.pathname);

        if (method.toUpperCase() === "GET") {
            params.append('ajax', 1);
            // Add ajax param to differentiate between and ajax requests and page request.
            // Otherwise Chrome caches these results as normal pages and returns them from cache if the back button is pressed
            url = addSearchParamsToUrl(url, params);
        }

        let request = fetch(url, {
            method: method,
            headers: {
                'pragma': 'no-cache',
                'cache-control': 'no-cache',
                ...options.fetchHeaders
            },
            ...(method.toUpperCase() !== "GET" ? {
                body: new URLSearchParams(params)
            } : {})
        });

        triggerWith('fetch.ajax-form', request, baseElement);


        let targetsByResultId = {};
        results.forEach((element) => {
            let resultId = element.dataset.resultId || 'default';
            if (targetsByResultId[resultId]) {
                targetsByResultId[resultId] = [...targetsByResultId[resultId], element];
            } else {
                targetsByResultId[resultId] = [element];
            }
        });

        asyncAppend(
            {
                target: targetsByResultId,
                loading: loadingElements,
                notifications: notifications
            },
            request
        )
            .then((result) => {
                let content = result.html || result.content;
                if (content && result.success !== false) {
                    trigger('success.ajax-form', baseElement);
                    errorAreas.map(setAttribute('hidden', 'hidden'));

                    let newForm = findIn(selectors.form, baseElement);
                    if (newForm) {
                        form = newForm;
                    }
                } else {
                    trigger('failed.ajax-form', baseElement);
                    errorAreas.map(removeAttribute('hidden'));
                }

                triggerWith('fetched.ajax-form', result, baseElement);
            })
            .catch(() => {
            });

        // Unpack json response body if the promise was created via fetch
        // Otherwise the HTTP-Server error is not caught.
        // The fetch promise itself resolves (even with a http error)
        request.then(response => (response
            && response.json
            && typeof response.json === 'function'
            && response.clone
            && typeof response.clone === 'function')
            ? response.clone().json()
            : response
        ).catch((error, requestState) => {
            if (!error || error.name !== 'AbortError') {
                console.error(error);
                trigger('failed.ajax-form', baseElement);
                errorAreas.map(removeAttribute('hidden'));
            }
        });

        return request;
    }

    function submitForm(form) {
        return submit(
            form.dataset.action || form.getAttribute('action'),
            form.dataset.method || form.getAttribute('method'),
        )
    }

    function submit(action, method) {
        action = action || form.dataset.action || form.getAttribute('action');
        method = method || form.dataset.method || form.getAttribute('method');

        // create promise to resolve/reject in right order (important for loading-indicator with multiple submissions)
        let readyToSubmit = new Promise(function (resolve, reject) {
            if (pendingRequest && pendingRequest.abort) {
                pendingRequest.abort();
                pendingRequest.catch(resolve);
                pendingRequest = null;
            } else {
                resolve();
            }
        });

        readyToSubmit.then(function () {
            if (typeof options.validator === "function" && !options.validator({
                baseElement,
                form
            })) {
                return;
            }

            trigger('submit.ajax-form', baseElement);

            action = action || form.dataset.action || form.getAttribute('action');
            method = method || form.dataset.method || form.getAttribute('method');
            let formDataEntries = getFormDataEntries();
            let params = new URLSearchParams(formDataEntries);

            options.onSubmit({
                $element: baseElement,
                options,
                formData: getFormData()
            });

            let url = new URL(location.href);
            url.searchParams.delete('page');
            url = addSearchParamsToUrl(url, params);

            pendingRequest = load(action, method, params, url);

            pendingRequest
                .then(() => pendingRequest = null)
                .catch((error) => {
                    if (!error || error.name !== 'AbortError') {
                        pendingRequest = null;
                    }
                });
        });
    }

    function getFormData() {
        return createFormData([form, ...findAllIn(selectors.additionalForm, baseElement)]);
    }

    function getFormDataEntries() {
        return createFormDataEntries([form, ...findAllIn(selectors.additionalForm, baseElement)]);
    }

    let api = {
        submit,
        getFormData,
        getFormDataEntries // todo add to doku
    };

    baseElement.dataset.ajaxForm = api;

    return api;
}

function addSearchParamsToUrl(url, searchParams) {
    url = new URL(url, location.origin);

    let searchParamsArray = Array.from(searchParams);
    searchParamsArray.forEach(([name]) => url.searchParams.delete(name));
    searchParamsArray.forEach(([name, value]) => url.searchParams.append(name, value));

    return url;
}

function createFormData(forms) {
    let formData = new FormData();
    forms.map(form => {
        for (var pair of formDataEntries(form)) {
            formData.append(...pair);
        }
    });

    return formData;
}

function createFormDataEntries(forms) {
    let formDataArray = [];

    forms.map(form => {
        // unchecked checkboxes and radios needs to be added manually to formDataArray
        let selectors = findAllIn('input[type="radio"], input[type="checkbox"]', form);
        let selectorNames = [];

        selectors.map(function(selector) {
            selectorNames.push(selector.name);
        });

        for (var pair of formDataEntries(form)) {
            formDataArray.push(pair);
        }

        let existingNames = formDataArray.map(entry => entry[0]);
        selectorNames = [...new Set(selectorNames)];

        selectorNames.forEach(function (name) {
            let newEntry = [name, ""];
            if(!existingNames.includes(name)) {
                formDataArray.push(newEntry);
            } else {
                formDataArray.filter(item => item !== newEntry);
            }
        });
    });

    return formDataArray;
}

function transformDataSetOptions(options = {}) {
    let transformedOptions = {...options};

    if (transformedOptions.fetchHeaders) {
        try {
            transformedOptions.fetchHeaders = JSON.parse(transformedOptions.fetchHeaders)
        } catch (e) {
            transformedOptions.fetchHeaders = {};
        }
    }

    return transformedOptions;
}
